import { Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import {
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from "@ionic/angular";
import { format, parseISO } from "date-fns";
import {
  Observable,
  Subscription,
  debounceTime,
  lastValueFrom,
  pairwise,
  startWith,
} from "rxjs";
import { newGuid } from "src/app/utils/guid";
import { ROUTES } from "src/app/utils/routes";
import { decimalValidator, integerValidator } from "src/app/utils/validators";
import { DeleteActionService } from "../delete-filter-actions.service";
import { GeolocationService } from "../geolocation.service";
import { StorageService } from "../storage.service";
import {
  CustomerModel,
  CustomerOrderDataModel,
  CustomerOrderDetModel,
  CustomerOrderService,
  ItemPriceListModel,
  ItemService,
  LocationService,
} from "../swagger";
import { UserDataService } from "../user/user.service";
import { FormManager } from "./form-manager";
import { DataService } from "../preload/data.service";
import { OrderItemsCacheService } from "../preload/order-items-cache.service";

export const customerOrderCreateDraftKey = "customerOrderDraft:create";
export const customerOrderUpdateDraftKeyPrefix = "customerOrderDraft:update:";
export const clearAllCustomerOrderDrafts = async (
  storage: StorageService,
): Promise<void> => {
  try {
    await storage.remove(customerOrderCreateDraftKey);
    const keys = await storage.keys();
    const keysToRemove = keys.filter((key) =>
      key.startsWith(customerOrderUpdateDraftKeyPrefix),
    );
    await Promise.all(keysToRemove.map((key) => storage.remove(key)));
  } catch (e) {
    // Ignore storage errors (private mode, quota, etc.).
  }
};

@Injectable()
export class CreateOrderForm {
  form: FormGroup;
  user: any;
  itemsPriceList: ItemPriceListModel[];
  selectedCustomer: CustomerModel;
  currentDate: Date;
  resource: any;
  someThingChange: boolean = false;
  canCreate: boolean;
  canUpdate: boolean;
  canCopyCustomerOrders: boolean;
  isOrderVoided: boolean;
  shipDateValue = format(new Date(), "yyyy-MM-dd");
  formattedShipDate = "";
  minDate: string;
  maxDate = format(new Date(2031, 11, 31), "yyyy-MM-dd");
  selectedItemSetting: any = undefined;
  itemsList: any[] = [];
  itemSearchTerm: string = "";
  private formChangesSub?: Subscription;
  private customerNumberChangeSub?: Subscription;
  private isInitializingForm: boolean = false;

  get expenseLines(): FormArray {
    return this.form.get("items") as FormArray;
  }

  constructor(
    private dataService: DataService,
    private orderItemsCache: OrderItemsCacheService,
    private router: Router,
    private loadingCtrl: LoadingController,
    private formManager: FormManager,
    private itemService: ItemService,
    private locationService: LocationService,
    private userService: UserDataService,
    private customerOrderService: CustomerOrderService,
    private geolocationService: GeolocationService,
    private deleteAction: DeleteActionService,
    private toastCtrl: ToastController,
    private alertCtrl: AlertController,
    private modalController: ModalController,
    private storageService: StorageService,
    private fb: FormBuilder,
  ) {}

  async initializeForm(user: any, order?: CustomerOrderDataModel) {
    this.isInitializingForm = true;
    this.itemSearchTerm = "";
    if (!order || !order.customerOrderId) {
      this.isOrderVoided = false;
    } else if (
      order.workflowStatusName === "Pending" ||
      order.workflowStatusName === "Submitted"
    ) {
      this.isOrderVoided = false;
      // don't make form disabled for status Pending and Submitted.
    } else {
      this.isOrderVoided = true;
    }
    this.resource = order;
    this.user = user;
    this.canCopyCustomerOrders = await this.resolveCanCopyCustomerOrders();
    let currentDate = format(new Date(), "yyyy-MM-dd");
    let data: CustomerOrderDataModel = { ...order };
    // A new order needs an identity before the server assigns one, so an
    // insert that gets re-sent is recognised as the same order rather than
    // creating a second. An order the server already knows keeps whatever it
    // holds — inventing a value here would send one it has never seen.
    const orderGuid =
      data.guid || (data.customerOrderId ? undefined : newGuid());
    this.form = this.fb.group({
      customerOrderId: [data.customerOrderId, []],
      guid: [orderGuid],
      customerNumber: [data.customerNumber, [Validators.required]],
      customerOrderDate: [
        data && data.customerOrderDate ? data.customerOrderDate : currentDate,
        [Validators.required],
      ],
      customerOrderShipDate: [
        data.customerOrderShipDate,
        [Validators.required],
      ],
      customerPO: [
        data.customerPO,
        [Validators.required, Validators.maxLength(60)],
      ],
      comments: [data.comments || "", [Validators.maxLength(500)]],
      total: [data.total, [Validators.required]],
      userId: [user.userId, [Validators.required]],
      statusId: [1, [Validators.required]],
      workflowStatusId: [data.workflowStatusId, [Validators.required]],
      items: this.fb.array([]),
    });

    if (order && this.isOrderVoided) {
      this.form.disable({ emitEvent: false });
      setTimeout(() => {
        this.form.markAsUntouched();
        this.form.markAsPristine();
      }, 500);
    }

    if (order && order.customerOrderShipDate) {
      this.minDate = undefined;
      this.dateChanged(order.customerOrderShipDate);
    } else {
      this.formattedShipDate = "";
      this.shipDateValue = format(new Date(), "yyyy-MM-dd");
      this.minDate = format(new Date(), "yyyy-MM-dd");
    }

    try {
      if (order && order.customerNumber) {
        this.currentDate = order.customerOrderDate
          ? new Date(order.customerOrderDate)
          : new Date();
        // The order response carries no locationId, so resolve the full
        // customer to find the location its item list must be scoped to.
        this.selectedCustomer = this.findCustomerByNumber(
          order?.customerNumber,
        ) || {
          customerName: order?.customerName,
          customerNumber: order?.customerNumber,
        };
        await this.getPriceListByCustomers(order?.customerNumber);
        if (order.items && order.items.length) {
          for (const item of order.items) {
            await this.addExpenseLinesForm(item, false, true);
          }
        }
      } else {
        this.itemsPriceList = [];
        this.currentDate = new Date();
        // this.form.controls.customerOrderShipDate.setValue(format(new Date(), "yyyy-MM-dd"));
      }

      this.setupDraftAutosave();
      // Reset dirty state after initialization so drafts only save on user changes.
      this.form.markAsPristine();
      this.form.markAsUntouched();

      if (this.customerNumberChangeSub) {
        this.customerNumberChangeSub.unsubscribe();
        this.customerNumberChangeSub = undefined;
      }

      this.customerNumberChangeSub =
        this.form?.controls.customerNumber.valueChanges
          .pipe(startWith(undefined), pairwise())
          .subscribe((valuesArray) => {
            const newVal = valuesArray[1];
            const oldVal = valuesArray[0];
            if (newVal !== oldVal && oldVal) {
              this.changeCustomer(this.user, valuesArray);
            }
          });
    } finally {
      this.isInitializingForm = false;
    }
  }

  dateChanged(value) {
    this.shipDateValue = value;
    this.form.controls.customerOrderShipDate.setValue(value);
    this.form.controls.customerOrderShipDate.updateValueAndValidity(value);
    this.formattedShipDate = format(parseISO(value), "MMM dd yyyy");
  }

  async addExpenseLinesForm(
    item?: CustomerOrderDetModel,
    saveDraft: boolean = true,
    isInitializing: boolean = false,
  ) {
    if (this.itemsList && this.itemsList.length && item && item.itemNumber) {
      const found = this.itemsList.find(
        (it) => item.itemNumber === it.itemNumber,
      );
      item = {
        ...item,
        repositoryImageURL:
          found && found.repositoryImageURL && found.repositoryImageURL.length
            ? found.repositoryImageURL
            : [],
      } as any;
    }
    let formGroup = await this.buildExpenseLinesForm(item);
    this.addInArrayItem(formGroup, item, saveDraft, isInitializing);
  }

  addInArrayItem(
    formGroup: FormGroup,
    item?: CustomerOrderDetModel,
    saveDraft: boolean = true,
    isInitializing: boolean = false,
  ) {
    this.ensureUniqueLineGuid(formGroup);
    if (item && item.customerOrderId) {
      this.expenseLines.push(formGroup);
      this.selectedItemChange(formGroup, item, isInitializing);
    } else {
      this.expenseLines.insert(0, formGroup);
    }
    if (!this.isOrderVoided) {
      // don't make form disabled for status Pending and Submitted.
    } else {
      formGroup.disable({ emitEvent: false });
    }
    if (saveDraft) {
      void this.saveOrderDraftFromForm();
    }
  }

  /**
   * The item modal builds each new line by copying controls from the same
   * source group, so adding several without closing it hands every one of them
   * that group's guid — and the server refuses the save because two lines claim
   * a single identity. Only lines it has not stored yet get reassigned:
   * rewriting a stored line's guid would let a retried save insert it again,
   * which is the very thing the guid exists to prevent.
   */
  private ensureUniqueLineGuid(formGroup: FormGroup): void {
    if (formGroup.get("customerOrderDetId")?.value) return;
    const control = formGroup.get("guid");
    const guid = control?.value;
    if (!control || !guid) return;
    const taken = this.expenseLines.controls.some(
      (line) => line !== formGroup && line.get("guid")?.value === guid,
    );
    if (taken) {
      control.setValue(newGuid());
    }
  }

  buildExpenseLinesForm(item?: CustomerOrderDetModel | any): FormGroup {
    // Same reasoning as the order guid: a line the server has never stored
    // needs its own identity so a re-sent save updates that row instead of
    // inserting a duplicate. Lines that already carry a customerOrderDetId
    // keep what the server holds.
    const lineGuid =
      (item && item.guid) ||
      (item && item.customerOrderDetId ? undefined : newGuid());
    return this.fb.group({
      guid: [lineGuid],
      itemNumber: [item && item.itemNumber, [Validators.required]],
      description: [item && item.description],
      quantity: [
        {
          value: item && item.quantity,
          disabled: item && item.quantity ? false : true,
        },
        [Validators.required, integerValidator({ required: true, min: 1 })],
      ],
      uOfM: [
        {
          value: item && item.uOfM,
          disabled: item && item.uOfM ? false : true,
        },
        [Validators.required],
      ],
      unitPrice: [
        {
          value: item && item.unitPrice,
          disabled: item && item.unitPrice ? false : true,
        },
        [Validators.required, decimalValidator({ required: true, min: 0 })],
      ],
      amount: [item && item.amount],
      notes: [(item && item.notes) || "", [Validators.maxLength(120)]],
      userId: [this.user.userId, [Validators.required]],
      statusId: [1, [Validators.required]],
      customerOrderId: [
        item && item.customerOrderId ? item.customerOrderId : 0,
      ],
      customerOrderDetId: [
        item && item.customerOrderDetId ? item.customerOrderDetId : 0,
      ],
      isPriceOverwrite: [true],
      isUpdateRequired: [true],
      uOfMList: [],
      selectedItem: [
        item && item.description && item.itemNumber
          ? {
              itemNumber: item.itemNumber,
              itemDescription: item.description,
              inventoryCount: item.inventoryCount ? item.inventoryCount : "",
              repositoryImageURL:
                item.repositoryImageURL && item.repositoryImageURL.length
                  ? item.repositoryImageURL
                  : [],
            }
          : undefined,
      ],
    });
  }

  async selectedExpenseItemChange(expenseItem: any, lineIndex: number) {
    this.expenseLines.controls[lineIndex]
      .get("itemNumber")
      .setValue(expenseItem);
  }

  async deleteExpenseLine(
    index: number,
    expenseLine: FormGroup,
    closeModal: boolean = false,
  ) {
    if (expenseLine.value.customerOrderId) {
      let data = expenseLine as any;
      let message = "Are you sure you want to delete this Item?";
      let deleteFn = async () => {
        expenseLine.controls.statusId.setValue(3);
        expenseLine.controls.isUpdateRequired.setValue(true);
        this.form.updateValueAndValidity();
        this.form.markAllAsTouched();
        this.form.markAsDirty();
        setTimeout(() => {
          this.countTotal();
        }, 500);
      };
      await this.deleteAction.onDelete(data, deleteFn, message);
    } else {
      this.expenseLines.removeAt(index);
    }
    void this.saveOrderDraftFromForm();
    if (closeModal) {
      this.modalController.dismiss();
    }
  }

  async changeCustomer(user: any, values: any[]) {
    if (this.expenseLines.length > 1) {
      let message =
        "Are you sure you want to change or remove customer if you do that change it will reset the item list?";
      let alert = await this.alertCtrl.create({
        header: "What would you like to do?",
        message: message,
        buttons: [
          { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
          {
            text: "Yes",
            role: "confirm",
            cssClass: "confirm-btn-delete",
          },
        ],
        mode: "md",
        cssClass: "delete-alert",
      });
      await alert.present();
      let { role } = await alert.onDidDismiss();
      if (role == "confirm") {
        this.selectedCustomer = undefined;
        await this.initializeForm(user);
        this.itemsPriceList = [];
        this.orderItemsCache.clearSelection();
      } else {
        this.form.controls.customerNumber.setValue(values[0]);
      }
    }
  }

  public countTotalAmount(formGroup: FormGroup) {
    let totalAmount = 0;
    if (formGroup) {
      const unitPrice = parseFloat(formGroup.value.unitPrice);
      const quantity = parseFloat(formGroup.value.quantity);

      if (!isNaN(unitPrice) && !isNaN(quantity)) {
        totalAmount = unitPrice * quantity;
      }

      formGroup.get("amount").setValue(totalAmount.toFixed(2));
    }
  }

  public countTotal() {
    let totalAmount = 0;
    this.expenseLines.value.map((data) => {
      if (data.amount != null && data.statusId == 1)
        totalAmount = totalAmount + parseFloat(data.amount);
    });
    if (this.form) {
      const currentTotal = parseFloat(this.form.controls.total.value || 0);
      if (currentTotal !== totalAmount) {
        this.form.controls.total.setValue(totalAmount, { emitEvent: false });
      }
    }
    return totalAmount.toFixed(2);
  }

  public countTotalQuantity() {
    let totalQuantity = 0;
    this.expenseLines.value.map((data) => {
      if (data.amount != null && data.statusId == 1)
        totalQuantity = totalQuantity + parseFloat(data.quantity);
    });
    return totalQuantity.toFixed(2);
  }

  async openToast(message: string, color: string = "dark") {
    if (message) {
      let toast = await this.toastCtrl.create({
        message: message,
        buttons: [{ text: "Done", role: "cancel" }],
        duration: 3000,
        color: color,
      });
      await toast.present();
    }
  }

  async createPending() {
    let alert = await this.alertCtrl.create({
      header: "Set customer order to Pending State?",
      message:
        "Are you sure you want to set this customer order to Pending State?",
      buttons: [
        { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
        {
          text: "Yes, Pending & Close",
          role: "confirm-close",
          cssClass: "pending-btn",
        }
        // ,
        // {
        //   text: "Yes, Pending",
        //   role: "confirm",
        //   cssClass: "pending-btn",
        // },
      ],
      mode: "md",
      cssClass: "delete-alert alert-submit-order",
    });
    await alert.present();
    let { role } = await alert.onDidDismiss();
    if (role === "confirm") {
      this.form.controls.workflowStatusId.setValue(1);
      await this.create(false);
    }
    if (role === "confirm-close") {
      this.form.controls.workflowStatusId.setValue(1);
      await this.create(true);
    }
  }

  async createSubmit() {
    let alert = await this.alertCtrl.create({
      header: "Set customer order to Submit?",
      message: "Are you sure you want to submit this customer order?",
      buttons: [
        { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
        {
          text: "Yes, Submit",
          role: "confirm",
          cssClass: "confirm-btn",
        },
      ],
      mode: "md",
      cssClass: "delete-alert",
    });
    await alert.present();
    let { role } = await alert.onDidDismiss();
    if (role === "confirm") {
      this.form.controls.workflowStatusId.setValue(2);
      await this.create();
    }
  }

  async createVoid() {
    let message = "Are you sure you want to void this customer order?";
    let headerMessage = "Void Customer Order?";
    let deleteFn = async () => {
      let id = this.resource.customerOrderId;
      await this.customerOrderService
        .apiCustomerorderVoidIdDelete({
          id: id,
        })
        .toPromise();
      await this.clearOrderDraftForCurrentForm();
      this.form = undefined;
      this.router.navigate([ROUTES.CUSTOMER_ORDERS_CO_LIST_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    };
    await this.deleteAction.onDelete(
      this.resource,
      deleteFn,
      message,
      true,
      headerMessage,
      "Voided Successfully",
      "Yes, Void",
    );
  }

  async createCopy() {
    const customerOrderNumber = this.resource?.customerOrderNumber;
    if (!customerOrderNumber) return;
    let alert = await this.alertCtrl.create({
      header: "Copy Customer Order",
      message: `Are you sure to Copy Customer Order #${customerOrderNumber}?`,
      buttons: [
        { text: "Cancel", role: "cancel", cssClass: "cancel-btn" },
        { text: "Yes, Copy", role: "confirm", cssClass: "confirm-btn" },
      ],
      mode: "md",
      cssClass: "delete-alert",
    });
    await alert.present();
    let { role } = await alert.onDidDismiss();
    if (role !== "confirm") return;

    let loading = await this.loadingCtrl.create({ message: "loading..." });
    await loading.present();
    try {
      // The endpoint takes the order number as a query parameter and answers
      // with a message naming the new order, so it is surfaced verbatim
      // rather than replaced with a generic success message.
      const result = await lastValueFrom(
        this.customerOrderService.apiCustomerorderCopyPost({
          customerOrderNumber: customerOrderNumber,
        }),
      );
      await loading.dismiss();
      const message =
        result?.message || `Customer Order ${customerOrderNumber} copied`;
      await this.openToast(message, "primary");
      this.form = undefined;
      this.router.navigate([ROUTES.CUSTOMER_ORDERS_CO_LIST_PAGE], {
        queryParams: {
          reload: "true",
        },
        queryParamsHandling: "merge", // remove to replace all query params by provided
      });
    } catch (e) {
      await loading.dismiss();
      this.deleteAction.alertDeleteError(
        "Unable to copy this customer order. Please try again.",
        "Copy Error",
      );
    }
  }

  async create(navigateToList: boolean = true) {
    let data = this.form?.value;
    data.items = this.expenseLines.value;
    this.dropEmptyGuids(data);
    let submitFn = async () => {
      let result: Observable<any>;
      let res: any;
      if (data.customerOrderId) {
        result = this.customerOrderService.apiCustomerorderUpdatePut({
          customerOrderUpdateModel: data,
        });
        res = await lastValueFrom(result);
      } else {
        data.geoLocation =
          await this.geolocationService.getCurrentGeoLocation();
        result = this.customerOrderService.apiCustomerorderInsertPost({
          customerOrderInsertModel: data,
        });
        res = await lastValueFrom(result);
      }
      await this.clearOrderDraftForCurrentForm();
      if (navigateToList) {
        this.form = undefined;
        this.router.navigate([ROUTES.CUSTOMER_ORDERS_CO_LIST_PAGE], {
          queryParams: {
            reload: "true",
          },
          queryParamsHandling: "merge", // remove to replace all query params by provided
        });
      } else {
        if (res && res.customerOrderId) {
          this.form = undefined;
          this.router.navigate(
            [
              ROUTES.CUSTOMER_ORDERS_CO_DETAIL_PAGE(
                res.customerOrderId.toString(),
              ),
            ],
            {
              queryParams: {
                reload: "true",
              },
              queryParamsHandling: "merge", // remove to replace all query params by provided
            },
          );
        } else if (data && data.customerOrderId) {
          let user = await this.userService.getCurrentUser();
          this.form = undefined;
          const result = await lastValueFrom(
            this.customerOrderService.apiCustomerorderIdGet({
              id: data.customerOrderId as any,
            }),
          );
          setTimeout(async () => {
            await this.initializeForm(user, result);
          }, 300);
        }
      }
    };
    let toast: string;
    if (data.customerOrderId) {
      toast = "Updated successfully";
    } else {
      toast = "Added successfully";
    }
    await this.formManager.submit(this.form!, submitFn, toast);
  }

  /**
   * Records created before guids existed have none, and an absent form control
   * reads as null. The API types guid as a uuid without marking it nullable,
   * so send the key only when there is a value behind it rather than risking a
   * null the server may refuse to deserialize.
   */
  private dropEmptyGuids(data: any): void {
    if (data && !data.guid) {
      delete data.guid;
    }
    for (const item of (data && data.items) || []) {
      if (item && !item.guid) {
        delete item.guid;
      }
    }
  }

  findCustomerByNumber(customerNumber: string): CustomerModel | undefined {
    if (!customerNumber) return undefined;
    return (this.dataService.allCustomers.getValue() || []).find(
      (customer) => customer.customerNumber === customerNumber,
    );
  }

  // Copying is gated on the AllowCopyCustomerOrders user setting alone; a
  // failed settings lookup denies rather than silently granting the action.
  async resolveCanCopyCustomerOrders(): Promise<boolean> {
    try {
      const value = await this.userService.getUserSettingValue(
        "UserSettings",
        "AllowCopyCustomerOrders",
      );
      return (value || "").toLowerCase() === "yes";
    } catch (e) {
      return false;
    }
  }

  // Resolves which location's items to load: the customer's own location, else
  // the user's default location, else the user's first assigned location.
  // `fromFallback` is true when the customer had no location, so callers can
  // tell the user which location was used instead.
  async resolveLocation(customer?: CustomerModel): Promise<ResolvedLocation> {
    if (customer?.locationId) {
      return {
        locationId: customer.locationId,
        locationName: customer.locationIdentifier || undefined,
        fromFallback: false,
      };
    }
    try {
      const user = await this.userService.getCurrentUser();
      let list: any[] = [];
      if (user?.userId) {
        const locations = (await lastValueFrom(
          this.locationService.apiLocationUserIdGet({ id: user.userId }),
        )) as any;
        list = Array.isArray(locations) ? locations : [];
      }

      const settings = await this.userService.getCurrentUserSettings();
      const defaultLocation = (settings || []).find(
        (s: any) =>
          s.userSettingName === "DefaultLocation" && s.userSettingValue,
      );
      if (defaultLocation?.userSettingValue) {
        const id = parseInt(defaultLocation.userSettingValue, 10);
        if (!isNaN(id)) {
          const match = list.find((l) => l?.locationId === id);
          return {
            locationId: id,
            locationName: match?.locationIdentifier || undefined,
            fromFallback: true,
          };
        }
      }

      if (list.length && list[0]?.locationId) {
        return {
          locationId: list[0].locationId,
          locationName: list[0].locationIdentifier || undefined,
          fromFallback: true,
        };
      }
    } catch (e) {
      // Fall through to an unscoped /item/all when no location can be resolved.
    }
    return { locationId: undefined, fromFallback: false };
  }

  async getPriceListByCustomers(id: string) {
    let loadingView = await this.loadingCtrl.create({ message: "loading..." });
    await loadingView.present();
    try {
      // A failed items load only costs inventory counts and images, so it must
      // not abort the price list that decides which items are selectable.
      const location = await this.resolveLocation(this.selectedCustomer);
      await this.orderItemsCache.load(location.locationId).catch(() => {});
      if (location.fromFallback && location.locationId) {
        const label = location.locationName || `Location ${location.locationId}`;
        this.openToast(`Loading items using location: ${label}`, "dark");
      }
      let result = await lastValueFrom(
        this.itemService.apiItemPricelistbycustomerCustomerNumberGet({
          customerNumber: id,
        }),
      );
      this.itemsPriceList = result as any;
      await loadingView.dismiss();
    } catch (e) {
      this.openToast("No price list found for that customer.", "red");
      await loadingView.dismiss();
      this.itemsPriceList = [];
    }
  }

  selectedItemChange(
    formGroup: FormGroup,
    item: any,
    isInitializing: boolean = false,
  ) {
    formGroup.controls.itemNumber.setValue(item?.itemNumber);
    let selectedItemPriceList: ItemPriceListModel[] = [];
    if (item && item.itemNumber) {
      selectedItemPriceList = this.itemsPriceList.filter(
        (val) => item.itemNumber === val.itemNumber,
      );
      formGroup.controls.selectedItem.setValue({
        itemNumber: item.itemNumber,
        itemDescription: item.itemDescription || item.description,
        inventoryCount: item.inventoryCount,
        repositoryImageURL: item.repositoryImageURL
          ? item.repositoryImageURL
          : [],
      });
    }
    formGroup.controls.uOfMList.setValue(selectedItemPriceList);
    let found = selectedItemPriceList.filter((val) => val.sellingUOFM === 1);
    let foundSellingUOFM: ItemPriceListModel =
      found && found.length ? found[0] : undefined;
    if (!item || (item && !item.customerOrderId)) {
      this.setItemControlValues(formGroup, foundSellingUOFM);
    } else {
      setTimeout(() => {
        this.countTotalAmount(formGroup);
        formGroup.updateValueAndValidity();
        this.form.updateValueAndValidity();
        if (!isInitializing) {
          this.form.markAllAsTouched();
          this.form.markAsDirty();
        }
      }, 300);
    }
    if (item && item.itemNumber) {
      this.setSelectedItemSetting(item.itemNumber);
    } else {
      this.selectedItemSetting = undefined;
    }
  }

  setSelectedItemSetting(itemNumber: any) {
    if (itemNumber) {
      this.selectedItemSetting = this.itemsPriceList.find(
        (item) => item.itemNumber == itemNumber,
      );
    }
  }

  setItemControlValues(
    formGroup: FormGroup,
    foundSellingUOFM: ItemPriceListModel,
  ) {
    formGroup.controls.description.setValue(
      foundSellingUOFM ? foundSellingUOFM.itemDescription : "",
    );
    let quantity = formGroup.controls.quantity.value;
    formGroup.controls.quantity.setValue(
      foundSellingUOFM ? (quantity ? quantity : 1) : 0,
    );
    formGroup.controls.uOfM.setValue(
      foundSellingUOFM ? foundSellingUOFM.uofm : "",
    );
    formGroup.controls.unitPrice.setValue(
      foundSellingUOFM ? foundSellingUOFM.pricePerUOFM : 0,
    );
    if (!foundSellingUOFM) {
      formGroup.controls.quantity.disable();
      formGroup.controls.uOfM.disable();
      formGroup.controls.unitPrice.disable();
    } else {
      formGroup.controls.quantity.enable();
      formGroup.controls.uOfM.enable();
      formGroup.controls.unitPrice.enable();
    }
    setTimeout(() => {
      this.countTotalAmount(formGroup);
      formGroup.updateValueAndValidity();
      this.form.updateValueAndValidity();
      this.form.markAllAsTouched();
      this.form.markAsDirty();
    }, 300);
  }

  async getOrderCreateDraft(): Promise<CustomerOrderDraft | null> {
    return await this.readOrderDraft(customerOrderCreateDraftKey);
  }

  async getOrderUpdateDraft(
    customerOrderId: number,
  ): Promise<CustomerOrderDraft | null> {
    if (!customerOrderId) return null;
    return await this.readOrderDraft(
      this.getOrderUpdateDraftKey(customerOrderId),
    );
  }

  async clearOrderCreateDraft(): Promise<void> {
    await this.clearOrderDraft(customerOrderCreateDraftKey);
  }

  async clearOrderUpdateDraft(customerOrderId: number): Promise<void> {
    if (!customerOrderId) return;
    await this.clearOrderDraft(this.getOrderUpdateDraftKey(customerOrderId));
  }

  clearFormChangeSubscription(): void {
    if (this.formChangesSub) {
      this.formChangesSub.unsubscribe();
      this.formChangesSub = undefined;
    }
    if (this.customerNumberChangeSub) {
      this.customerNumberChangeSub.unsubscribe();
      this.customerNumberChangeSub = undefined;
    }
  }

  private getOrderUpdateDraftKey(customerOrderId: number): string {
    return `${customerOrderUpdateDraftKeyPrefix}${customerOrderId}`;
  }

  private setupDraftAutosave(): void {
    this.clearFormChangeSubscription();
    if (!this.form) return;

    this.formChangesSub = this.form.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        if (this.shouldSaveOrderDraft()) {
          this.someThingChange = true;
          void this.saveOrderDraftFromForm();
        }
      });
  }

  private shouldSaveOrderDraft(): boolean {
    if (!this.form || this.isOrderVoided || this.isInitializingForm) return false;
    return this.form.dirty || this.someThingChange;
  }

  private async saveOrderDraftFromForm(): Promise<void> {
    if (!this.form || this.isOrderVoided || this.isInitializingForm) return;

    const formValue = this.form.getRawValue();
    const customerOrderId = formValue?.customerOrderId
      ? formValue.customerOrderId
      : null;

    const draft: CustomerOrderDraft = {
      meta: {
        customerOrderId,
        customerNumber:
          this.selectedCustomer?.customerNumber || formValue?.customerNumber,
        customerName:
          this.selectedCustomer?.customerName || this.resource?.customerName,
        customerOrderNumber: this.resource?.customerOrderNumber || "",
        savedAt: new Date().toISOString(),
      },
      form: {
        ...formValue,
        items: this.expenseLines ? this.expenseLines.getRawValue() : [],
      },
    };

    const key = customerOrderId
      ? this.getOrderUpdateDraftKey(customerOrderId)
      : customerOrderCreateDraftKey;
    await this.writeOrderDraft(key, draft);
  }

  async clearOrderDraftForCurrentForm(): Promise<void> {
    if (!this.form) return;
    const customerOrderId = this.form.getRawValue()?.customerOrderId;
    if (customerOrderId) {
      await this.clearOrderUpdateDraft(customerOrderId);
    } else {
      await this.clearOrderCreateDraft();
    }
  }

  private async readOrderDraft(
    key: string,
  ): Promise<CustomerOrderDraft | null> {
    try {
      const raw = await this.storageService.get(key);
      if (!raw) return null;
      return JSON.parse(raw) as CustomerOrderDraft;
    } catch (e) {
      return null;
    }
  }

  private async writeOrderDraft(
    key: string,
    draft: CustomerOrderDraft,
  ): Promise<void> {
    try {
      await this.storageService.set(key, JSON.stringify(draft));
    } catch (e) {
      // Ignore storage errors (quota, private mode, etc.).
    }
  }

  private async clearOrderDraft(key: string): Promise<void> {
    try {
      await this.storageService.remove(key);
    } catch (e) {
      // Ignore storage errors (private mode, quota, etc.).
    }
  }
}

export type ResolvedLocation = {
  locationId?: number;
  locationName?: string;
  fromFallback: boolean;
};

export type CustomerOrderDraft = {
  meta: {
    customerOrderId: number | null;
    customerNumber: string | number;
    customerName: string;
    customerOrderNumber: string | number;
    savedAt: string;
  };
  form: any;
};
